export default function TermsPage() {
  const css = `
    :root { --bg:#0a0a0f; --surface:#13131a; --border:#1e1e2e; --accent:#00e5a0; --text:#e8e8f0; --muted:#6b6b8a; }
    * { box-sizing:border-box; margin:0; padding:0; }
    body { background:var(--bg); color:var(--text); font-family:"DM Sans",sans-serif; min-height:100vh; }
    a { color:var(--accent); text-decoration:none; }
    nav { background:var(--surface); border-bottom:1px solid var(--border); padding:0 24px; height:56px; display:flex; align-items:center; justify-content:space-between; position:sticky; top:0; z-index:100; }
    .nav-logo { font-size:11px; letter-spacing:4px; font-weight:700; color:var(--accent); }
    .nav-links { display:flex; gap:24px; }
    .nav-links a { font-size:13px; color:var(--muted); }
    .nav-links a:hover { color:var(--text); }
    .wrap { max-width:720px; margin:0 auto; padding:48px 24px 80px; }
    h1 { font-size:clamp(26px,4vw,38px); font-weight:900; letter-spacing:-1px; margin-bottom:8px; color:var(--text); }
    h2 { font-size:18px; font-weight:700; margin:28px 0 8px; color:var(--text); }
    p, li { color:var(--muted); line-height:1.75; font-size:15px; margin-bottom:10px; }
    ul { padding-left:20px; margin-bottom:12px; }
    li { margin-bottom:6px; }
    .hero { padding:48px 0 36px; border-bottom:1px solid var(--border); margin-bottom:36px; }
    .tag { font-size:10px; letter-spacing:3px; text-transform:uppercase; color:var(--accent); font-weight:700; margin-bottom:12px; }
    .highlight { background:var(--surface); border:1px solid var(--border); border-left:3px solid var(--accent); border-radius:8px; padding:16px 20px; margin:24px 0; }
    .highlight p { color:var(--text); margin:0; font-size:14px; }
    footer { border-top:1px solid var(--border); padding:36px 24px; text-align:center; }
    .footer-links { display:flex; justify-content:center; gap:20px; flex-wrap:wrap; margin-bottom:12px; }
    .footer-links a { font-size:13px; color:var(--muted); }
    .footer-copy { font-size:12px; color:var(--muted); }
    @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
    .fade { animation:fadeUp 0.5s ease both; }
  `;

  return (
    <>
      <style>{css}</style>
      <nav>
        <a href="/" className="nav-logo">ClearMyDebt</a>
        <div className="nav-links">
          <a href="/about.html">About</a>
          <a href="/faq.html">FAQ</a>
          <a href="/contact.html">Contact</a>
          <a href="/" style={{ color: 'var(--accent)', fontWeight: 700 }}>Get My Plan</a>
        </div>
      </nav>

      <div className="wrap">
        <div className="hero fade">
          <div className="tag">Legal</div>
          <h1>Terms of Service</h1>
          <p style={{ color: 'var(--muted)', fontSize: '14px', marginTop: '8px' }}>Last updated: March 2026 &nbsp;·&nbsp; Weelco Inc.</p>
        </div>

        <div className="highlight">
          <p><strong>Short version:</strong> You pay once and get your debt payoff plan PDF. No subscriptions, no account required. Your financial data never leaves your browser. This tool is for informational purposes only — not financial advice.</p>
        </div>

        <h2>1. Acceptance of Terms</h2>
        <p>By accessing or using ClearMyDebt (the "Service"), operated at clearmydebt.app by Weelco Inc. ("we", "us", or "our"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service.</p>

        <h2>2. Description of the Service</h2>
        <p>ClearMyDebt is a debt payoff calculator that helps users understand their repayment options. The Service includes a free interactive calculator and an optional one-time purchase of a downloadable PDF debt payoff plan ("the Plan").</p>
        <ul>
          <li>The calculator is free to use with no account required.</li>
          <li>The PDF Plan is available for a one-time payment. Pricing varies by country and is displayed in your local currency before purchase.</li>
          <li>Purchasing the Plan grants lifetime access to download the generated PDF for your current debt inputs.</li>
        </ul>

        <h2>3. Purchases and Payment</h2>
        <p>Payment is processed securely by Stripe. We do not store your payment card details. By completing a purchase you agree to Stripe's Terms of Service.</p>
        <ul>
          <li><strong style={{ color: 'var(--text)' }}>One-time fee:</strong> You are charged once. There are no subscriptions, recurring charges, or hidden fees.</li>
          <li><strong style={{ color: 'var(--text)' }}>Pricing:</strong> The price shown before checkout is the total amount you will be charged.</li>
          <li><strong style={{ color: 'var(--text)' }}>Currency:</strong> Prices are displayed in your local currency where supported.</li>
        </ul>

        <h2>4. Refund Policy</h2>
        <p>All purchases are final. Due to the digital nature of the product, we do not offer refunds once a PDF plan has been generated and made available for download.</p>
        <p>If you experience a technical issue that prevents you from accessing or downloading your Plan, contact us at <a href="mailto:support@clearmydebt.app">support@clearmydebt.app</a> within 48 hours of purchase and we will make it right.</p>

        <h2>5. Informational Purpose Only</h2>
        <p>The Service and all outputs — including the PDF Plan — are provided for informational and educational purposes only. Nothing on this Service constitutes financial, legal, tax, or professional advice.</p>
        <ul>
          <li>Calculations assume fixed interest rates and consistent monthly payments. Actual results may vary.</li>
          <li>We are not a licensed financial advisor, credit counsellor, or debt management service.</li>
          <li>You should consult a qualified financial professional before making significant financial decisions.</li>
        </ul>

        <h2>6. Your Data</h2>
        <p>Financial information you enter (balances, interest rates, payments) is processed entirely within your browser. This data is never transmitted to or stored on our servers. See our <a href="/privacy.html">Privacy Policy</a> for full details.</p>

        <h2>7. Intellectual Property</h2>
        <p>All content, design, and code on this Service — including the generated PDF Plans — are the property of Weelco Inc. and protected by applicable intellectual property laws. You may not reproduce, distribute, or resell any part of the Service or its outputs without our written permission.</p>
        <p>The PDF Plan generated for you is licensed for your personal use only.</p>

        <h2>8. Disclaimer of Warranties</h2>
        <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that:</p>
        <ul>
          <li>The Service will be error-free or uninterrupted</li>
          <li>Calculations will be perfectly accurate in all circumstances</li>
          <li>Results will match your actual debt payoff timeline</li>
        </ul>

        <h2>9. Limitation of Liability</h2>
        <p>To the maximum extent permitted by applicable law, Weelco Inc. shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service, even if we have been advised of the possibility of such damages. Our total liability shall not exceed the amount you paid for the Service.</p>

        <h2>10. Governing Language and Jurisdiction</h2>
        <p>These Terms are written in English and English shall be the governing language in case of any dispute. These Terms do not designate a specific national jurisdiction. Any disputes shall be resolved through good-faith negotiation or, if necessary, binding arbitration under internationally recognised rules.</p>

        <h2>11. Changes to These Terms</h2>
        <p>We may update these Terms periodically. Material changes will be indicated by updating the "Last updated" date. Continued use of the Service after changes constitutes acceptance of the updated Terms.</p>

        <h2>12. Contact</h2>
        <p>For questions about these Terms, contact us at: <a href="mailto:legal@clearmydebt.app">legal@clearmydebt.app</a></p>
        <p>Data controller: ClearMyDebt (clearmydebt.app), operated by Weelco Inc.</p>
      </div>

      <footer>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: 'var(--accent)', fontWeight: 700, marginBottom: '16px' }}>ClearMyDebt</div>
        <div className="footer-links">
          <a href="/">Calculator</a>
          <a href="/about.html">About</a>
          <a href="/faq.html">FAQ</a>
          <a href="/contact.html">Contact</a>
          <a href="/terms/">Terms of Service</a>
          <a href="/privacy.html">Privacy Policy</a>
        </div>
        <div className="footer-copy">2026 Weelco Inc. All rights reserved.</div>
      </footer>
    </>
  );
}
